<template>
  <button :title="title" :class="$style.button" :aria-label="title" @click.prevent.enter="onClick">
    <SvgIcon :svg="HeartFillSvg" :class="[$style.icon, isLiked && $style.isLiked]" />
  </button>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { likeEvent, unlikeEvent } from '@/js/lib/api/event';
import { useEventStore } from '@/js/lib/store/event';
import { useUserStore } from '@/js/lib/store/user';
import { loading } from '@/js/lib/ui';
import { signinSignup } from '@/js/lib/ui/signin-signup';
import SvgIcon from '@/js/components/molecules/SvgIcon.vue';
import HeartFillSvg from '@/svgs/vue/heart-fill.svg';

const props = defineProps<{
  eventId: number;
}>();
const userStore = useUserStore();
const eventStore = useEventStore();
const isLoading = ref(false);
const isLiked = computed(() => eventStore.likedEventIds.includes(props.eventId));
const title = computed(() => (isLiked.value ? 'お気に入りから外す' : 'お気に入りに追加'));
const like = async () => {
  eventStore.addLiked(props.eventId);
  await likeEvent({ eventId: props.eventId }).catch(() => {
    eventStore.removeLiked(props.eventId);
  });
};
const unlike = async () => {
  eventStore.removeLiked(props.eventId);
  await unlikeEvent({ eventId: props.eventId }).catch(() => {
    eventStore.addLiked(props.eventId);
  });
};
const onClick = async () => {
  if (userStore.isAuthenticated) {
    if (isLoading.value) {
      return;
    }

    isLoading.value = true;
    await (isLiked.value ? unlike() : like());
    isLoading.value = false;
  } else {
    signinSignup({
      next: window.location.href,
      callback: async () => {
        await loading({
          asyncFunc: async () => {
            await like();
            window.location.reload();
          },
          text: 'ログインしています',
        });
      },
    });
  }
};
</script>

<style module lang="scss">
.button {
  display: block;
}

.icon {
  cursor: pointer;
  display: block;
  fill: var(--color-black-alpha30);
  padding: 2px 5px;
  stroke: var(--color-white);
  stroke-width: 1.6;
  width: 38px;

  @media (--sp) {
    -webkit-tap-highlight-color: var(--color-black-alpha0);
  }

  &:hover {
    fill: var(--color-black-alpha40);
  }

  &.isLiked {
    fill: var(--action-button-heart-color);
  }
}
</style>
